import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { ApiError, Error } from '@app/core/models';
import { ErrorService } from '@app/core/services/error';
import { extractTouchedChanges } from '@app/core/utils/extract-touched-changes';
import { camelCase } from 'lodash-es';
import { BehaviorSubject, combineLatest, merge, Subscription } from 'rxjs';
import { debounceTime, filter, map, tap } from 'rxjs/operators';

type ErrorIntermediate = {
	code: string;
	data?: any;
};

type ControlOptions = {
	dirty: boolean;
	touched: boolean;
	invalid: boolean;
};

const controlOptionsDefaults: ControlOptions = {
	dirty: true,
	touched: true,
	invalid: true,
};

type ValidationErrorOptions = {
	prefix?: string;
	filter?: string;
};

const validationErrorOptionsDefaults: ValidationErrorOptions = {};

type ErrorOptions = {
	prefix?: string;
	filter?: string;
	sourceFilter?: string;
};

const errorOptionsDefaults: ErrorOptions = {};

type ApiErrorOptions = {
	prefix?: string;
	filter?: string;
	sourceFilter?: string;
};

const apiErrorOptionsDefaults: ErrorOptions = {};

export type Options = {
	control?: Partial<ControlOptions>;
	validationError?: Partial<ValidationErrorOptions>;
	apiError?: Partial<ApiErrorOptions>;
	error?: Partial<ErrorOptions>;
};

const optionsDefaults: Options = {
	control: controlOptionsDefaults,
	validationError: validationErrorOptionsDefaults,
	apiError: apiErrorOptionsDefaults,
	error: errorOptionsDefaults,
};

@Component({
	selector: 'gls-error',
	styleUrls: ['error.component.scss'],
	templateUrl: 'error.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorComponent implements OnInit, OnDestroy {
	@Input()
	get errors(): Error[] {
		return this._errors$.getValue();
	}
	set errors(errors: Error[]) {
		this._errors$.next(errors);
	}
	private _errors$ = new BehaviorSubject<Error[]>([]);

	@Input()
	get validationErrors(): ValidationErrors | null {
		return this._validationErrors$.getValue();
	}
	set validationErrors(validationErrors: ValidationErrors | null) {
		this._validationErrors$.next(validationErrors);
	}
	private _validationErrors$ = new BehaviorSubject<ValidationErrors | null>(null);

	@Input()
	get apiErrors(): ApiError[] {
		return this._apiErrors$.getValue();
	}
	set apiErrors(apiErrors: ApiError[]) {
		this._apiErrors$.next(apiErrors);
	}
	private _apiErrors$ = new BehaviorSubject<ApiError[]>([]);

	@Input() control?: AbstractControl | null;
	private _controlErrors$ = new BehaviorSubject<ValidationErrors | null>(null);

	@Input() options?: Options;

	private _options: Options = optionsDefaults;

	public errorsCombined$ = combineLatest([
		this._errors$.pipe(map(errors => this.transformErrors(errors))),
		this._controlErrors$.pipe(map(errors => this.transformValidationErrors(errors))),
		this._validationErrors$.pipe(map(errors => this.transformValidationErrors(errors))),
		this._apiErrors$.pipe(map(errors => this.transformApiErrors(errors))),
	]).pipe(map(([errors, controlErrors, validationErrors, apiErrors]) => [...errors, ...controlErrors, ...validationErrors, ...apiErrors]));

	private readonly _subscriptions: Subscription[] = [];

	constructor(private errorService: ErrorService) {}

	ngOnInit(): void {
		this._options = {
			...optionsDefaults,
			...this.options,
		};

		if (this.control instanceof AbstractControl) {
			this._subscriptions.push(
				merge(this.control.statusChanges, extractTouchedChanges(this.control))
					.pipe(debounceTime(100))
					.subscribe(status => {
						if (this.control instanceof AbstractControl) {
							if ((this._options.control?.dirty ? this.control.dirty : false) || (this._options.control?.touched ? this.control.touched : false)) {
								// console.log(this.control);
								this._controlErrors$.next(this.control.errors);
							}
						}
					})
			);
		}
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach(subscription => subscription.unsubscribe());
	}

	private transformValidationErrors = (validationErrors: ValidationErrors | null): Error[] => {
		if (validationErrors === null) return [];

		return Object.entries(validationErrors)
			.filter(([key, error]) => {
				if (this._options.validationError?.filter) {
					return key === this._options.validationError.filter;
				}
				return true;
			})
			.map(
				([key, data]) =>
					({
						code: `${this._options.validationError?.prefix ?? ''}${key}`,
						data,
					} as Error)
			)
			.map(error => ({
				...error,
				message: this.errorService.get(error),
			}));
	};

	private transformApiErrors = (apiErrors: ApiError[] | null): Error[] => {
		if (apiErrors === null) return [];

		return apiErrors
			.filter(error => {
				let filterResult = true;
				if (this._options.apiError?.filter) {
					filterResult &&= error.code === this._options.apiError.filter;
				}
				if (this._options.apiError?.sourceFilter && error.source?.parameters) {
					filterResult &&= error.source.parameters?.includes(this._options.apiError.sourceFilter);
				}
				return filterResult;
			})
			.map(error => ({
				code: `${this._options.apiError?.prefix ?? ''}${error.code.split(':').map(camelCase).join(':')}`,
				data: error,
			}))
			.map(error => ({
				...error,
				message: this.errorService.get(error),
			}));
	};

	private transformErrors = (errors: Error[] | null): Error[] => {
		if (errors === null) return [];

		return errors
			.filter(error => {
				let filterResult = true;
				if (this._options.error?.filter) {
					filterResult &&= error.code === this._options.error.filter;
				}
				if (this._options.error?.sourceFilter && error.source?.parameters) {
					filterResult &&= error.source.parameters?.includes(this._options.error.sourceFilter);
				}
				return filterResult;
			})
			.map(error => ({
				code: `${this._options.error?.prefix ?? ''}${error.code}`,
				data: error,
			}))
			.map(error => ({
				...error,
				message: this.errorService.get(error),
			}));
	};
}
